import React from 'react';
import { ArrowDownIcon, CheckIcon, XIcon } from 'lucide-react';
import { CTAButton } from '../CTAButton';
import { methodSteps, withSystem, withoutSystem } from '../../data/content';
const consultationFlow = ['Open the Clinical Route.', 'Identify the scenario.', 'Follow the relevant pathway.', 'Review the decision points.', 'Document your assessment.', 'Plan the follow-up.'];
const openQuestions = ['What should I look at first?', 'What information actually matters here?', 'What changes my clinical approach?', 'What should I reassess if the patient doesn’t respond?', 'How should I structure the follow-up?'];
export function Method() {
  return <section className="w-full bg-white py-14 sm:py-20" data-id="element-193" data-mp="1nota4z5zqp0r">
      <div className="mx-auto w-full max-w-4xl px-5" data-id="element-194" data-mp="1nqe4oosb27ym">
        <p className="text-[0.7rem] font-bold uppercase tracking-[0.16em] text-brand-600" data-id="element-195" data-mp="3m4xsmvyd5mx7">
          This is not another medical ebook
        </p>
        <h2 className="mt-3 font-display text-[1.7rem] font-extrabold leading-tight tracking-tight text-navy-900 sm:text-4xl" data-id="element-196" data-mp="3i3m0aio3unlb">
          It’s a clinical decision system designed around the actual
          consultation.
        </h2>
        <p className="mt-4 text-[0.97rem] leading-relaxed text-slate-600" data-id="element-197" data-mp="2yhyv58xe9jiq">
          Instead of giving you hundreds of pages of information and expecting
          you to remember everything, Clinical Route for Erectile Dysfunction™
          gives you a structured pathway — so you can spend less time searching
          for information and more time focusing on the patient in front of you.
        </p>

        <div className="mt-10 rounded-3xl bg-navy-950 p-5 sm:p-8" data-id="element-198" data-mp="3hh2yc1298j2h">
          <p className="text-[0.7rem] font-bold uppercase tracking-[0.16em] text-cyan-bright" data-id="element-199" data-mp="3heqlu3f9n0wt">
            The Clinical Route Method™
          </p>
          <h3 className="mt-2 font-display text-xl font-extrabold text-white sm:text-2xl" data-id="element-200" data-mp="d2cxo8pz13mn">
            A simple framework for navigating the consultation
          </h3>

          <ol className="mt-6" data-id="element-201" data-mp="rpbmdvxg1jxo">
            {methodSteps.map((step, index) => <li key={step.code} data-id="element-202" data-mp="rn7j9g7dcoyg" data-mpi={index}>
                <div className="flex items-start gap-4 rounded-2xl border border-white/10 bg-white/[0.04] px-4 py-4" data-id="element-203" data-mp="nn6xtblurdhw">
                  <span className="font-display text-sm font-extrabold text-brand-500" data-id="element-204" data-mp="inkkl260xx93">
                    {step.code}
                  </span>
                  <div data-id="element-205" data-mp="drwtuibw8w1h">
                    <h4 className="font-display text-base font-extrabold uppercase tracking-tight text-white" data-id="element-206" data-mp="8q7it2oo8ide">
                      {step.title}
                    </h4>
                    <p className="mt-1 text-[0.9rem] leading-relaxed text-slate-400" data-id="element-207" data-mp="3xmsib4z4nem">
                      {step.text}
                    </p>
                  </div>
                </div>
                {index < methodSteps.length - 1 ? <div className="flex justify-center py-1.5" data-id="element-208" data-mp="3m5lx3omfrw5l">
                    <ArrowDownIcon className="h-4 w-4 text-brand-500" aria-hidden="true" data-id="element-209" data-mp="3m76qvc0xfb2e" />
                  </div> : null}
              </li>)}
          </ol>
        </div>

        <div className="mt-12" data-id="element-210" data-mp="3c049p0r5gaot">
          <h3 className="font-display text-[1.4rem] font-extrabold leading-tight text-navy-900 sm:text-3xl" data-id="element-211" data-mp="3c38hs97qjqkr">
            What physicians need isn’t more information
          </h3>
          <p className="mt-2 text-[0.97rem] leading-relaxed text-slate-600" data-id="element-212" data-mp="3lytogknr1f0u">
            They need a better way to navigate the information they already
            have. A patient walks into your office with erectile dysfunction.
            You have limited time. The patient expects answers. And the clinical
            picture may involve several different factors.
          </p>

          <div className="mt-6 rounded-3xl border border-slate-200 bg-slate-50 p-5" data-id="element-213" data-mp="332abfk9tr6z0">
            <p className="text-[0.7rem] font-bold uppercase tracking-[0.14em] text-brand-600" data-id="element-214" data-mp="25ka6lsz970zk">
              Instead of starting from scratch
            </p>
            <ol className="mt-4 space-y-2" data-id="element-215" data-mp="g47q2qucgwma">
              {consultationFlow.map((line, index) => <li key={line} className="flex items-center gap-3 rounded-xl bg-white px-4 py-2.5 text-[0.92rem] font-medium text-navy-800 ring-1 ring-slate-200" data-id="element-216" data-mp="b5eljnx1cfy9" data-mpi={index}>
                  <span className="font-display text-xs font-extrabold text-slate-400" data-id="element-217" data-mp="l2ssmwx2jsdw">
                    {String(index + 1).padStart(2, '0')}
                  </span>
                  {line}
                </li>)}
            </ol>
            <p className="mt-4 font-display text-base font-extrabold text-navy-900" data-id="element-218" data-mp="usugpopf9ast">
              A complex consultation becomes a structured process.
            </p>
          </div>
        </div>

        <div className="mt-12" data-id="element-219" data-mp="13ulhcf53urft">
          <h3 className="font-display text-[1.4rem] font-extrabold leading-tight text-navy-900 sm:text-3xl" data-id="element-220" data-mp="3ak8f385brnub">
            Why physicians choose a structured system
          </h3>
          <p className="mt-2 text-[0.97rem] leading-relaxed text-slate-600" data-id="element-221" data-mp="35n9sdanzqj65">
            Because clinical uncertainty often comes from information overload.
            You don’t necessarily need another 300-page guideline. You need to
            know:
          </p>
          <ul className="mt-4 space-y-2" data-id="element-222" data-mp="1bcjq2hfgpyhz">
            {openQuestions.map(question => <li key={question} className="rounded-2xl border-l-4 border-brand-500 bg-slate-50 px-4 py-3 text-[0.92rem] font-semibold text-navy-800" data-id="element-223" data-mp="1gbculkcruf60">
                {question}
              </li>)}
          </ul>
          <p className="mt-4 text-[0.95rem] font-semibold text-brand-700" data-id="element-224" data-mp="seyyh2lbclto">
            That’s exactly what the Clinical Route is designed to organize.
          </p>
        </div>

        <div className="mt-12" data-id="element-225" data-mp="2msgpizkxg0tg">
          <h3 className="text-center font-display text-sm font-extrabold uppercase tracking-[0.14em] text-navy-900" data-id="element-226" data-mp="2ms8df9wmozjb">
            The difference is the process
          </h3>
          <div className="mt-6 grid gap-5 lg:grid-cols-2" data-id="element-227" data-mp="2qa4fspqb2ozv">
            <div className="rounded-3xl border border-rose-200 bg-rose-50 p-5" data-id="element-228" data-mp="2qbp9kd4sq3wo">
              <h4 className="flex items-center gap-2 font-display text-base font-extrabold uppercase tracking-tight text-rose-700" data-id="element-229" data-mp="2lajhmhiymko7">
                <XIcon className="h-4 w-4" aria-hidden="true" data-id="element-230" data-mp="2geax0olztz9n" />
                Without a system
              </h4>
              <ul className="mt-4 space-y-2" data-id="element-231" data-mp="390vve90vzbmp">
                {withoutSystem.map(line => <li key={line} className="rounded-xl bg-white px-4 py-2.5 text-[0.9rem] text-slate-700 ring-1 ring-rose-100" data-id="element-232" data-mp="341sqqpotj7f9">
                    {line}
                  </li>)}
              </ul>
            </div>
            <div className="rounded-3xl border border-emerald-200 bg-emerald-50 p-5" data-id="element-233" data-mp="2v7o46igpwi5j">
              <h4 className="flex items-center gap-2 font-display text-base font-extrabold uppercase tracking-tight text-emerald-700" data-id="element-234" data-mp="3rthoilkeso3l">
                <CheckIcon className="h-4 w-4" aria-hidden="true" data-id="element-235" data-mp="19iada8y85woc" />
                With a structured route
              </h4>
              <ul className="mt-4 space-y-2" data-id="element-236" data-mp="1ei5286yfwc6r">
                {withSystem.map(line => <li key={line} className="rounded-xl bg-white px-4 py-2.5 text-[0.9rem] text-slate-700 ring-1 ring-emerald-100" data-id="element-237" data-mp="1iifhybc1agyt">
                    {line}
                  </li>)}
              </ul>
            </div>
          </div>
        </div>

        <div className="mx-auto mt-10 max-w-md" data-id="element-238" data-mp="1xeeq1lz58h3y">
          <CTAButton label="I want to decide with more clarity" note="Immediate digital access" data-id="element-239" data-mp="226y4el5nng9t" />
        </div>
      </div>
    </section>;
}